import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { Send, Square } from 'lucide-react';
import { v4 as uuid } from 'uuid';
import { pdf } from '@react-pdf/renderer';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import PageContent from '@bumble/app/components/PageContent';
import MessageBubble from '@bumble/app/components/agent-analyzer/MessageBubble';
import { Message, ToolResult } from '@bumble/app/components/agent-analyzer/types';
import { useAccount } from '@bumble/app/hooks/use-account';
import '@bumble/app/components/agent-analyzer/index.css';
import { NECP_SESSION_ID } from '@bumble/app/constants';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import MarkdownPdf from '@bumble/app/components/agent-analyzer/MarkdownPdf';
import { Cpe, Document as NodeDocument } from '@bumble/app/__generated__/data-contracts';

const WELCOME = `NetOps Engineer Coworker ready.

Describe your network issue and I'll investigate it systematically —
running diagnostic tools in parallel and guiding you to the root cause.

Try: Get me the round trip time from CPE <your CPE name> to 8.8.8.8`;

const MAX_COMPLETION_OPTIONS = 8;

interface ActiveMention {
  start: number;
  end: number;
  query: string;
}

interface CpeCompletionOption {
  id: string;
  name: string;
  entityName?: string;
}

function normalizeCompletionValue(value: string): string {
  return value.trim().toLowerCase();
}

function getActiveMention(value: string, cursor: number): ActiveMention | null {
  const beforeCursor = value.slice(0, cursor);
  const match = beforeCursor.match(/(^|\s)@([^\n@]*)$/);
  if (!match || match.index === undefined) return null;

  const mentionStart = match.index + match[1].length;
  return {
    start: mentionStart,
    end: cursor,
    query: match[2],
  };
}

interface SSEHandlers {
  onSessionId: (id: string) => void;
  onThinking: (text: string) => void;
  onToolStart: (
    payload: {
      name: string;
      input: Record<string, unknown>;
    },
  ) => void;
  onToolResult: (
    payload: {
      name: string;
      result: ToolResult;
    },
  ) => void;
  onToken: (text: string) => void;
  onDone: () => void;
  onError: (msg: string) => void;
}

interface UseSSE {
  stream: (
    text: string,
    sessionId: string | null,
    accessToken: string,
    handlers: SSEHandlers,
  ) => Promise<void>;

  abort: () => void;
}

interface StreamArgs {
  text: string;
  sessionId: string | null;
  accessToken: string;
  handlers: SSEHandlers;
}

export const useSSE = (): UseSSE => {
  const controllerRef = useRef<AbortController | null>(null);

  const mutation = useMutation({
    mutationFn: async ({
      text,
      sessionId,
      accessToken,
      handlers,
    }: StreamArgs) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const response = await fetch(`${import.meta.env.VITE_API}/necp-diagnose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({
          message: text,
          session_id: sessionId,
        }),
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      if (!response.body) {
        throw new Error('No response body');
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          handlers.onDone();
          return;
        }
        buffer += decoder.decode(value, {
          stream: true,
        });
        const events = buffer.split(/\r?\n\r?\n/);
        buffer = events.pop() || '';
        for (const eventChunk of events) {
          const lines = eventChunk.split(/\r?\n/);
          let eventType = '';
          let data = '';
          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventType = line.replace(/^event:\s*/, '');
            }
            if (line.startsWith('data:')) {
              data += line.replace(/^data:\s*/, '');
            }
          }
          if (!eventType) {
            continue;
          }
          switch (eventType) {
            case 'session_id': {
              const parsed = JSON.parse(data);
              handlers.onSessionId(parsed.session_id);
              break;
            }
            case 'thinking': {
              handlers.onThinking(data);
              break;
            }
            case 'tool_start': {
              handlers.onToolStart(JSON.parse(data));
              break;
            }
            case 'tool_result': {
              handlers.onToolResult(JSON.parse(data));
              break;
            }
            case 'token': {
              const parsed = JSON.parse(data);
              handlers.onToken(parsed.text);
              break;
            }
            case 'done': {
              handlers.onDone();
              return;
            }
            case 'error': {
              handlers.onError(data);
              return;
            }
          }
        }
      }
    },
  });

  return {
    stream: async (
      text,
      sessionId,
      accessToken,
      handlers,
    ) => {
      try {
        await mutation.mutateAsync({
          text,
          sessionId,
          accessToken,
          handlers,
        });
      } catch (error) {
        if (
          error instanceof DOMException &&
          error.name === 'AbortError'
        ) {
          return;
        }
        handlers.onError(
          error instanceof Error
            ? error.message
            : String(error),
        );
      }
    },
    abort: () => {
      controllerRef.current?.abort();
    },
  };
};

export const AgentAnalyzer = (): JSX.Element => {
  const api = useApi();

  const { storageToken } = useAccount();
  const session = storageToken();
  const accessToken = session.access_token;
  const [messages, setMessages] = useState<Message[]>([
    { id: uuid(), role: 'system', text: WELCOME },
  ]);
  const [input, setInput] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionIdInitialized, setSessionIdInitialized] = useState<boolean>(false);
  const [inputCursor, setInputCursor] = useState<number>(0);
  const [completionOpen, setCompletionOpen] = useState<boolean>(false);
  const [highlightedCompletionIndex, setHighlightedCompletionIndex] = useState<number>(0);
  const [selectedCompletionCpe, setSelectedCompletionCpe] = useState<CpeCompletionOption | null>(null);
  const [documentInsertionCursor, setDocumentInsertionCursor] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const activeAssistantId = useRef<string | null>(null);
  const toolCallCounter = useRef(0);

  const { stream, abort } = useSSE();

  useEffect(() => {
    const necp_session_id = localStorage.getItem(NECP_SESSION_ID);
    if (necp_session_id) {
      setSessionId(necp_session_id);
      setSessionIdInitialized(true);
    }
  }, []);

  const clearSession = useMutation(api.necpDeleteSessionApiV1NecpDeleteSessionPost, {
    onSuccess: () => {
      setMessages([{ id: uuid(), role: 'system', text: WELCOME }]);
      setSessionId(null);
      // The next session will be created locally from streaming, not resumed
      // from storage — so it must NOT be hydrated from server history. Leaving
      // this true lets the history query re-enable when the new session id
      // arrives mid-stream, which replaces the live messages and drops the
      // first answer ("Analyzing" then nothing).
      setSessionIdInitialized(false);
      localStorage.removeItem(NECP_SESSION_ID);
    },
    onError: apiError,
  });

  const { data } = useQuery(
    ['necpSessionHistory'],
    api.necpGetSessionHistoryApiV1NecpGetSessionHistoryGet.bind(null, {
      session_id: sessionId!
    }),
    { enabled: sessionIdInitialized && !!sessionId, cacheTime: 0 },
  );

  const { data: cpeCompletionData, isLoading: isCpeCompletionLoading } = useQuery(
    ['necpCpeCompletions'],
    api.getCpeApiV1GetCpeGet.bind(null, {}),
    { enabled: true, cacheTime: 0 },
  );

  const activeMention = useMemo(() => getActiveMention(input, inputCursor), [input, inputCursor]);

  const cpeCompletionOptions = useMemo<CpeCompletionOption[]>(() => (
    (cpeCompletionData?.data ?? [])
      .map((cpe: Cpe) => ({
        id: cpe.cpe_id,
        name: cpe.cpe_name,
        entityName: cpe.entity_name,
      }))
      .filter((cpe) => cpe.id && cpe.name)
  ), [cpeCompletionData]);

  const normalizedMentionQuery = normalizeCompletionValue(activeMention?.query ?? '');

  const exactCpeMatch = useMemo<CpeCompletionOption | null>(() => {
    if (!normalizedMentionQuery) return null;
    return cpeCompletionOptions.find((cpe) => (
      normalizeCompletionValue(cpe.id) === normalizedMentionQuery ||
      normalizeCompletionValue(cpe.name) === normalizedMentionQuery
    )) ?? null;
  }, [cpeCompletionOptions, normalizedMentionQuery]);

  const visibleCpeCompletions = useMemo<CpeCompletionOption[]>(() => {
    if (exactCpeMatch) return [];
    return cpeCompletionOptions
      .filter((cpe) => {
        if (!normalizedMentionQuery) return true;
        return (
          normalizeCompletionValue(cpe.id).includes(normalizedMentionQuery) ||
          normalizeCompletionValue(cpe.name).includes(normalizedMentionQuery) ||
          normalizeCompletionValue(cpe.entityName ?? '').includes(normalizedMentionQuery)
        );
      })
      .slice(0, MAX_COMPLETION_OPTIONS);
  }, [cpeCompletionOptions, exactCpeMatch, normalizedMentionQuery]);

  const documentCpeMatch = selectedCompletionCpe ?? exactCpeMatch;

  const { data: documentCompletionData, isLoading: isDocumentCompletionLoading } = useQuery(
    ['necpDocumentCompletions', documentCpeMatch?.id],
    api.getDocumentsApiV1GetDocumentsGet.bind(null, {
      node_id: documentCpeMatch?.id,
    }),
    { enabled: !!documentCpeMatch?.id, cacheTime: 0 },
  );

  const visibleDocumentCompletions = useMemo<NodeDocument[]>(() => (
    (documentCompletionData?.data ?? [])
      .filter((document: NodeDocument) => document.filename.toLowerCase().endsWith('.pcap'))
      .slice(0, MAX_COMPLETION_OPTIONS)
  ), [documentCompletionData]);

  const completionMode = documentCpeMatch ? 'documents' : 'cpes';
  const showCompletionPanel =
    completionOpen &&
    (!!activeMention || !!selectedCompletionCpe) &&
    !isStreaming;

  useEffect(() => {
    setHighlightedCompletionIndex(0);
  }, [completionMode, activeMention?.query, documentCpeMatch?.id]);

  useEffect(() => {
    // Only hydrate from server history when resuming a persisted session.
    // Never overwrite the message list mid-stream — a late history fetch would
    // wipe the live conversation and orphan the streaming assistant message.
    if (isStreaming) return;
    const newMessages: Message[] = (data?.data?.history ?? []).map((h: any) => ({
      text: h.text,
      role: h.role,
      id: uuid(),
    }));
    setMessages(([{ id: uuid(), role: 'system', text: WELCOME }] as Message[]).concat(newMessages));
  }, [data]);

  const scrollToBottom = useCallback((): void => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'instant' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const appendToken = useCallback((text: string): void => {
    setMessages(prev =>
      prev.map(m =>
        m.id === activeAssistantId.current ? { ...m, text: m.text + text } : m,
      ),
    );
  }, []);

  const addThinking = useCallback((text: string): void => {
    setMessages(prev =>
      prev.map(m =>
        m.id === activeAssistantId.current
          ? { ...m, thinking: (m.thinking ?? '') + text }
          : m,
      ),
    );
  }, []);

  const addToolCall = useCallback((name: string, input: Record<string, unknown>): void => {
    const callId = `${name}-${Date.now()}-${toolCallCounter.current++}-${crypto.randomUUID()}`

    setMessages(prev =>
      prev.map(m =>
        m.id === activeAssistantId.current
          ? {
              ...m,
              toolCalls: [
                ...(m.toolCalls ?? []),
                {
                  callId,
                  name,
                  input,
                  status: 'running',
                  result: null,
                },
              ],
            }
          : m
      )
    )
  }, []);

  const updateToolResult = useCallback((name: string, result: ToolResult): void => {
    setMessages(prev =>
      prev.map(m => {
        if (m.id !== activeAssistantId.current) return m;
        return {
          ...m,
          toolCalls: (m.toolCalls ?? []).map(tc =>
            tc.name === name && tc.status === 'running'
              ? {
                  ...tc,
                  result,
                  status: (result as { error?: unknown })?.error ? 'error' : 'done',
                }
              : tc,
          ),
        };
      }),
    );
  }, []);

  const handleClearHistory = () => {
    if (sessionId) {
      clearSession.mutate({ session_id: sessionId });
    }
  };

  const handleDownload = async () => {
    let markdown = '';
    for (const msg of messages) {
      switch (msg.role) {
        case 'assistant':
          markdown += `
## Assitant
${msg.text}

`;
          break;
        case 'user':
          markdown += `
## User
${msg.text}

`;
          break;
        default:
          markdown += `
#${msg.text}

`;
      }
    }
    // const blob = new Blob([markdown], {
    //   type: 'text/markdown;charset=utf-8',
    // });
    const blob = await pdf(
      <MarkdownPdf markdown={markdown} />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // a.download = `netops-cowork-${sessionId}.md`;
    a.download = `netops-cowork-${sessionId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const focusInputAt = useCallback((cursor: number): void => {
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(cursor, cursor);
      setInputCursor(cursor);
    });
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    setInputCursor(e.target.selectionStart ?? e.target.value.length);
    setSelectedCompletionCpe(null);
    setDocumentInsertionCursor(null);
    setCompletionOpen(true);
  };

  const handleSelectCpeCompletion = useCallback((cpe: CpeCompletionOption): void => {
    if (!activeMention) return;

    const token = `CPE ${cpe.name} (ID: ${cpe.id})`;
    const nextInput = `${input.slice(0, activeMention.start)}${token}${input.slice(activeMention.end)}`;
    const nextCursor = activeMention.start + token.length;
    setInput(nextInput);
    setSelectedCompletionCpe(cpe);
    setDocumentInsertionCursor(nextCursor);
    setCompletionOpen(true);
    focusInputAt(nextCursor);
  }, [activeMention, focusInputAt, input]);

  const handleSelectDocumentCompletion = useCallback((document: NodeDocument): void => {
    const insertionCursor = activeMention?.end ?? documentInsertionCursor ?? inputCursor;

    const safeFilename = document.filename.replace(/[\r\n]+/g, ' ').replace(/"/g, '\\"');
    const token = ` document "${safeFilename}" (${document.document_id})`;
    const nextInput = `${input.slice(0, insertionCursor)}${token}${input.slice(insertionCursor)}`;
    const nextCursor = insertionCursor + token.length;
    setInput(nextInput);
    setSelectedCompletionCpe(null);
    setDocumentInsertionCursor(null);
    setCompletionOpen(false);
    focusInputAt(nextCursor);
  }, [activeMention, documentInsertionCursor, focusInputAt, input, inputCursor]);

  const handleCompletionKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!showCompletionPanel) return false;

    const activeOptionsLength = completionMode === 'documents'
      ? visibleDocumentCompletions.length
      : visibleCpeCompletions.length;

    if (e.key === 'Escape') {
      e.preventDefault();
      setCompletionOpen(false);
      return true;
    }

    if (!activeOptionsLength) return false;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedCompletionIndex(prev => (prev + 1) % activeOptionsLength);
      return true;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedCompletionIndex(prev => (prev - 1 + activeOptionsLength) % activeOptionsLength);
      return true;
    }

    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      if (completionMode === 'documents') {
        const document = visibleDocumentCompletions[highlightedCompletionIndex] ?? visibleDocumentCompletions[0];
        if (document) handleSelectDocumentCompletion(document);
      } else {
        const cpe = visibleCpeCompletions[highlightedCompletionIndex] ?? visibleCpeCompletions[0];
        if (cpe) handleSelectCpeCompletion(cpe);
      }
      return true;
    }

    return false;
  };

  const handleSubmit = useCallback(async (): Promise<void> => {
    const text = input.trim();
    if (!text || isStreaming) return;

    setInput('');
    inputRef.current?.focus();
    setIsStreaming(true);
    setMessages(prev => [...prev, { id: uuid(), role: 'user', text }]);

    const aid = uuid();
    activeAssistantId.current = aid;
    setMessages(prev => [
      ...prev,
      { id: aid, role: 'assistant', text: '', toolCalls: [], thinking: '' },
    ]);

    await stream(text, sessionId, accessToken, {
      onSessionId: (id: string) => { setSessionId(id); localStorage.setItem(NECP_SESSION_ID, id); },
      onThinking: addThinking,
      onToolStart: ({ name, input: toolInput }: { name: string; input: Record<string, unknown> }) =>
        addToolCall(name, toolInput),
      onToolResult: ({ name, result }: { name: string; result: ToolResult }) =>
        updateToolResult(name, result),
      onToken: appendToken,
      onDone: () => {
        setIsStreaming(false);
        activeAssistantId.current = null;
      },
      onError: (msg: string) => {
        setMessages(prev => [
          ...prev,
          { id: uuid(), role: 'error', text: `Error: ${msg}` },
        ]);
        setIsStreaming(false);
        activeAssistantId.current = null;
      },
    });
  }, [
    input,
    isStreaming,
    sessionId,
    accessToken,
    stream,
    addThinking,
    addToolCall,
    updateToolResult,
    appendToken,
  ]);

  const lastMsg = messages[messages.length - 1];
  const showTypingDots =
    isStreaming &&
    lastMsg?.role === 'assistant' &&
    !lastMsg?.text &&
    !lastMsg?.toolCalls?.length;
  const isCompletionLoading = completionMode === 'documents'
    ? isDocumentCompletionLoading
    : isCpeCompletionLoading;
  const completionTitle = completionMode === 'documents'
    ? `Documents for ${documentCpeMatch?.name ?? documentCpeMatch?.id}`
    : 'CPEs';
  const completionEmptyText = completionMode === 'documents'
    ? 'No documents found for this CPE'
    : 'No matching CPEs';

  return (
    <PageContent>
      <div style={{ display: 'flex', height: '90vh', overflow: 'hidden' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <header
            style={{
              height: '44px',
              flexShrink: 0,
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '20px',
              paddingRight: '16px',
              gap: '10px',
              background: 'var(--bg-panel)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
              <span
                style={{
                  fontSize: '11px',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Diagnostic Session
              </span>
              {sessionId && (
                <span style={{ fontSize: '11px', color: 'var(--accent-cyan)' }}>
                  · {sessionId.slice(0, 8)}
                </span>
              )}
            </div>
            {isStreaming && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '11px',
                  color: 'var(--accent-amber)',
                }}
              >
                ● Analyzing
              </div>
            )}
            <Button
              onClick={handleDownload}
              variant="outlined"
              disabled={!sessionId}
              endIcon={<DownloadIcon fontSize="small" />}
            >
              Download
            </Button>
            <Button
              onClick={handleClearHistory}
              variant="outlined"
              disabled={!sessionId}
              endIcon={<DeleteIcon fontSize="small" />}
            >
              Delete
            </Button>
          </header>
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            {messages.map(msg => (
              <MessageBubble key={msg.id} msg={msg} />
            ))}
            {showTypingDots && (
              <div style={{ paddingLeft: '4px', color: 'var(--text-muted)', fontSize: '13px' }}>
                ● ● ●
              </div>
            )}
          </div>
          <div
            style={{
              flexShrink: 0,
              padding: '14px 20px',
              borderTop: '1px solid var(--border)',
              background: 'var(--bg-panel)',
            }}
          >
            <div style={{ position: 'relative' }}>
              {showCompletionPanel && (
                <div
                  role="listbox"
                  aria-label={completionTitle}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 'calc(100% + 8px)',
                    maxHeight: '280px',
                    overflowY: 'auto',
                    background: 'var(--bg-panel)',
                    border: '1px solid var(--border-bright)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--shadow-panel)',
                    zIndex: 10,
                  }}
                >
                  <div
                    style={{
                      padding: '8px 10px',
                      fontSize: '11px',
                      color: 'var(--text-muted)',
                      borderBottom: '1px solid var(--border)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {completionTitle}
                  </div>
                  {isCompletionLoading ? (
                    <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '12px' }}>
                      Loading…
                    </div>
                  ) : completionMode === 'documents' ? (
                    visibleDocumentCompletions.length ? (
                      visibleDocumentCompletions.map((document, index) => (
                        <button
                          key={document.document_id}
                          type="button"
                          role="option"
                          aria-selected={index === highlightedCompletionIndex}
                          onMouseEnter={() => setHighlightedCompletionIndex(index)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelectDocumentCompletion(document);
                          }}
                          style={{
                            width: '100%',
                            border: 'none',
                            borderBottom: '1px solid var(--border)',
                            background: index === highlightedCompletionIndex
                              ? 'var(--bg-hover)'
                              : 'var(--bg-panel)',
                            color: 'var(--text-primary)',
                            padding: '9px 10px',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontFamily: 'var(--font-mono)',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '13px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {document.filename}
                          </div>
                          <div
                            style={{
                              fontSize: '11px',
                              color: 'var(--text-muted)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {document.document_id}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '12px' }}>
                        {completionEmptyText}
                      </div>
                    )
                  ) : visibleCpeCompletions.length ? (
                    visibleCpeCompletions.map((cpe, index) => (
                      <button
                        key={cpe.id}
                        type="button"
                        role="option"
                        aria-selected={index === highlightedCompletionIndex}
                        onMouseEnter={() => setHighlightedCompletionIndex(index)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectCpeCompletion(cpe);
                        }}
                        style={{
                          width: '100%',
                          border: 'none',
                          borderBottom: '1px solid var(--border)',
                          background: index === highlightedCompletionIndex
                            ? 'var(--bg-hover)'
                            : 'var(--bg-panel)',
                          color: 'var(--text-primary)',
                          padding: '9px 10px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '13px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {cpe.name} {cpe.id}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            fontSize: '11px',
                            color: 'var(--text-muted)',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {cpe.entityName && (
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {cpe.entityName}
                            </span>
                          )}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '12px' }}>
                      {completionEmptyText}
                    </div>
                  )}
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-end',
                  background: 'var(--bg-surface)',
                  border: `1px solid ${isStreaming ? 'var(--accent-cyan-dim)' : 'var(--border-bright)'}`,
                  borderRadius: 'var(--radius-lg)',
                  padding: '10px 14px',
                  transition: 'border-color 0.2s',
                }}
              >
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onClick={e => {
                    setInputCursor(e.currentTarget.selectionStart ?? e.currentTarget.value.length);
                    setCompletionOpen(true);
                  }}
                  onKeyUp={e => {
                    setInputCursor(e.currentTarget.selectionStart ?? e.currentTarget.value.length);
                    setCompletionOpen(true);
                  }}
                  onKeyDown={e => {
                    if (handleCompletionKeyDown(e)) return;
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void handleSubmit();
                    }
                  }}
                  placeholder="Describe your network issue…"
                  rows={1}
                  style={{
                    flex: 1,
                    background: 'none',
                    border: 'none',
                    outline: 'none',
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '13px',
                    resize: 'none',
                    lineHeight: 1.5,
                    maxHeight: '120px',
                    overflowY: 'auto',
                  }}
                />
                <button
                  onClick={isStreaming ? abort : () => void handleSubmit()}
                  disabled={!isStreaming && !input.trim()}
                  style={{
                    flexShrink: 0,
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    border: 'none',
                    background: isStreaming
                      ? 'var(--accent-red-dim)'
                      : input.trim()
                      ? 'var(--accent-cyan)'
                      : 'var(--bg-raised)',
                    color: isStreaming
                      ? 'var(--accent-red)'
                      : input.trim()
                      ? 'var(--bg-void)'
                      : 'var(--text-muted)',
                    cursor: !isStreaming && !input.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}
                >
                  {isStreaming ? <Square size={13} /> : <Send size={13} />}
                </button>
              </div>
            </div>
            <div
              style={{
                fontSize: '10px',
                color: 'var(--text-muted)',
                marginTop: '6px',
                paddingLeft: '2px',
              }}
            >
              Enter to send · Shift+Enter for newline
            </div>
          </div>
        </div>
      </div>
    </PageContent>
  );
};
